import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { EmployeeIDBadge } from '../../../shared/components/common/Badges';
import RoleBadge from '../../../shared/components/common/RoleBadge';
import ConfirmationModal from '../../../shared/components/modals/ConfirmationModal';
import Modal from '../../../shared/components/modals/Modal';
import { formatPhoneNumber, removeCountryCode, validateIndianPhoneNumber } from '../../../utils/phoneFormatter';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import './ProfileSettings.css';

const LOCATION_SCOPED_ROLES = ['admin', 'manager', 'central_help_desk'];
const DEPARTMENT_SCOPED_ROLES = ['technician', 'coordinator', 'custodian'];

function ProfileSettings({ token, user, onProfilePictureUpdate }) {
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [imageKey, setImageKey] = useState(Date.now());
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditingContactNumber, setIsEditingContactNumber] = useState(false);
  const [contactNumber, setContactNumber] = useState(user?.contact_number || '');
  const [isUpdatingContact, setIsUpdatingContact] = useState(false);
  const [showContactSaveSuccess, setShowContactSaveSuccess] = useState(false);
  const [showRemovePictureConfirmation, setShowRemovePictureConfirmation] = useState(false);
  const [isRemovingPicture, setIsRemovingPicture] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [showDepartmentsModal, setShowDepartmentsModal] = useState(false);
  const [showLocationsModal, setShowLocationsModal] = useState(false);

  const isLocationScoped = LOCATION_SCOPED_ROLES.includes(user?.role);
  const isDepartmentScoped = DEPARTMENT_SCOPED_ROLES.includes(user?.role);
  const assignedDepartments = user?.departments || [];
  const assignedLocations = user?.locations || [];
  const showDepartmentsField = isLocationScoped || isDepartmentScoped;

  // Update contact number when user object changes (remove +91 for editing)
  useEffect(() => {
    const plainNumber = user?.contact_number ? removeCountryCode(user.contact_number) : '';
    setContactNumber(plainNumber);
  }, [user?.contact_number]);

  // Fetch profile picture with authentication and convert to blob URL
  // This is necessary because <img> tags don't send Authorization headers
  useEffect(() => {
    // Cleanup function to revoke old blob URLs (prevent memory leaks)
    return () => {
      if (profilePictureUrl) {
        URL.revokeObjectURL(profilePictureUrl);
      }
    };
  }, [user?.profile_picture]);

  // Separate useEffect to fetch and create blob URL
  useEffect(() => {
    const fetchProfilePicture = async () => {
      if (!user?.profile_picture || !token) {
        setProfilePictureUrl(null);
        return;
      }

      try {
        const response = await axios.get(API_ENDPOINTS.AUTH.PROFILE_PICTURE(user.id), {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          responseType: 'blob' // Important: tell axios to expect binary data
        });

        // Create blob URL from response
        const blobUrl = URL.createObjectURL(response.data);
        setProfilePictureUrl(blobUrl);

        // Update imageKey to force re-render
        const newKey = Date.now();
        setImageKey(newKey);
      } catch (error) {
        console.error('Error fetching profile picture:', error);
        setProfilePictureUrl(null);
      }
    };

    fetchProfilePicture();
  }, [user?.profile_picture, user?.id, token]);

  // Calculate password strength (same as PasswordResetRequired.js)
  const calculatePasswordStrength = (password) => {
    if (!password) return 0;

    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (password.length >= 12) strength += 1;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 1;

    return Math.min(strength, 5);
  };

  const getPasswordStrengthLabel = (strength) => {
    const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
    return labels[strength] || '';
  };

  const getPasswordStrengthColor = (strength) => {
    const colors = ['#d32f2f', '#f57c00', '#fbc02d', '#7cb342', '#388e3c', '#1b5e20'];
    return colors[strength] || '#999';
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    // Calculate strength only for new_password field
    if (name === 'new_password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
    setError('');
    setSuccess('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords match
    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('New passwords do not match');
      return;
    }

    // Validate password length
    if (passwordData.new_password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    // Validate new password is different from current password
    if (passwordData.current_password === passwordData.new_password) {
      setError('New password must be different from current password');
      return;
    }

    try {
      await axios.post(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setSuccess('Password changed successfully!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setIsChangingPassword(false);

      // Auto-hide success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error changing password:', error);
      setError(error.response?.data?.error || 'Failed to change password');
    }
  };

  const handleCancel = () => {
    setPasswordData({
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
    setError('');
    setSuccess('');
    setIsChangingPassword(false);
  };

  const handleSaveContactNumber = async () => {
    setError('');
    setSuccess('');

    // Validate contact_number using Indian phone number validation
    if (contactNumber) {
      const validation = validateIndianPhoneNumber(contactNumber);
      if (!validation.isValid) {
        setError(validation.error);
        return;
      }
    }

    setIsUpdatingContact(true);

    try {
      // Send plain 10 digits to API (database stores plain format)
      const plainNumber = contactNumber ? contactNumber.replace(/\D/g, '') : '';
      await axios.put(API_ENDPOINTS.AUTH.PROFILE, {
        contact_number: plainNumber
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Call onProfilePictureUpdate to refresh user data (it fetches /auth/me)
      // This must happen BEFORE showing success to ensure state updates
      if (onProfilePictureUpdate) {
        await onProfilePictureUpdate();
      }

      setIsEditingContactNumber(false);
      setShowContactSaveSuccess(true);

      // Auto-hide checkmark after 5 seconds
      setTimeout(() => setShowContactSaveSuccess(false), 5000);
    } catch (error) {
      console.error('Error updating contact number:', error);
      setError(error.response?.data?.error || 'Failed to update contact number');
    } finally {
      setIsUpdatingContact(false);
    }
  };

  const handleCancelContactNumber = () => {
    const plainNumber = user?.contact_number ? removeCountryCode(user.contact_number) : '';
    setContactNumber(plainNumber);
    setError('');
    setSuccess('');
    setIsEditingContactNumber(false);
  };

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];

    if (!file) {
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setUploadingPicture(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(API_ENDPOINTS.AUTH.PROFILE_PICTURE_UPLOAD, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Clean up old blob URL before creating new one
      if (profilePictureUrl) {
        URL.revokeObjectURL(profilePictureUrl);
        setProfilePictureUrl(null);
      }

      // IMPORTANT: Refresh user data FIRST before updating imageKey
      // This ensures user.profile_picture is set to true before the image loads
      if (onProfilePictureUpdate) {
        await onProfilePictureUpdate();
        // The useEffect will automatically fetch and create new blob URL
      }

      // Now force image refresh after user data is updated
      const newImageKey = Date.now();
      setImageKey(newImageKey);
      setSuccess('Profile picture updated successfully!');
      setTimeout(() => setSuccess(''), 3000);

      // Reset file input
      e.target.value = '';
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      setError(error.response?.data?.error || 'Failed to upload profile picture');
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleRemoveProfilePicture = () => {
    // Show confirmation modal instead of window.confirm
    setShowRemovePictureConfirmation(true);
  };

  const handleConfirmRemovePicture = async () => {
    setIsRemovingPicture(true);
    setUploadingPicture(true);
    setError('');
    setSuccess('');

    try {
      await axios.delete(API_ENDPOINTS.AUTH.PROFILE_PICTURE_DELETE, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Clean up blob URL when removing picture
      if (profilePictureUrl) {
        URL.revokeObjectURL(profilePictureUrl);
        setProfilePictureUrl(null);
      }

      // IMPORTANT: Refresh user data FIRST before updating imageKey
      // This ensures user.profile_picture is set to false/null before the image reloads
      if (onProfilePictureUpdate) {
        await onProfilePictureUpdate();
      }

      // Now force image refresh after user data is updated
      setImageKey(Date.now());
      setSuccess('Profile picture removed successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setShowRemovePictureConfirmation(false);
    } catch (error) {
      console.error('Error removing profile picture:', error);
      setError(error.response?.data?.error || 'Failed to remove profile picture');
    } finally {
      setUploadingPicture(false);
      setIsRemovingPicture(false);
    }
  };

  const handleCancelRemovePicture = () => {
    setShowRemovePictureConfirmation(false);
  };

  return (
    <div className="profile-settings">
      <div className="profile-settings-description-section">
        <h1>👤 Profile Settings</h1>
        <p>View your account details, update your contact number, and manage your password.</p>
      </div>

      <div className="profile-settings-body">
        {/* Profile Picture Section */}
        <div className="profile-settings-section">
          <h2 className="profile-settings-section-title">Profile Picture</h2>
          <div className="profile-picture-container">
            <div className="profile-picture-preview">
              {user.profile_picture && profilePictureUrl ? (
                <img
                  key={imageKey}
                  src={profilePictureUrl}
                  alt="Profile"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                  className="profile-pic-img"
                />
              ) : null}
              <div
                className="profile-pic-placeholder"
                style={{ display: (user.profile_picture && profilePictureUrl) ? 'none' : 'flex' }}
              >
                👤
              </div>
            </div>
            <div className="profile-picture-upload">
              <div className="profile-pic-actions action-palette">
                <button
                  type="button"
                  className="action-btn action-primary"
                  disabled={uploadingPicture}
                  onClick={() => document.getElementById('profile-pic-input')?.click()}
                >
                  <span className="action-icon">📷</span>
                  <span className="action-text">
                    {uploadingPicture
                      ? 'Uploading...'
                      : user.profile_picture
                        ? 'Change Picture'
                        : 'Upload Picture'}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={handleRemoveProfilePicture}
                  disabled={uploadingPicture || !user.profile_picture}
                  className="action-btn action-secondary profile-remove-pic-btn"
                  title={user.profile_picture ? "Remove profile picture" : "No profile picture to remove"}
                >
                  <span className="action-icon" aria-hidden="true">🗑️</span>
                  <span className="action-text">Remove</span>
                </button>
              </div>
              <input
                id="profile-pic-input"
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/gif"
                onChange={handleProfilePictureChange}
                disabled={uploadingPicture}
                style={{ display: 'none' }}
              />
              <p className="upload-hint">PNG, JPG or GIF (max 5MB)</p>
            </div>
          </div>
        </div>

        {/* User Information Section */}
        <div className="profile-settings-section">
          <h2 className="profile-settings-section-title">Account Information</h2>
          <div className="info-grid">
              <div className="info-field">
                <div className="field-header">
                  <span className="field-icon">👤</span>
                  <span className="field-label">Employee ID</span>
                </div>
                <div className="field-value">
                  <EmployeeIDBadge employeeId={user.employee_id} size="medium" />
                </div>
              </div>

              <div className="info-field">
                <div className="field-header">
                  <span className="field-icon">👤</span>
                  <span className="field-label">Username</span>
                </div>
                <div className="field-value">{user.username}</div>
              </div>

              <div className="info-field">
                <div className="field-header">
                  <span className="field-icon">👨‍💼</span>
                  <span className="field-label">Full Name</span>
                </div>
                <div className="field-value">{user.full_name}</div>
              </div>

              <div className="info-field">
                <div className="field-header">
                  <span className="field-icon">✉️</span>
                  <span className="field-label">Email Address</span>
                </div>
                <div className="field-value">{user.email}</div>
              </div>

              <div className="info-field">
                <div className="field-header">
                  <span className="field-icon">📞</span>
                  <span className="field-label">Contact Number</span>
                </div>
                {isEditingContactNumber ? (
                  <div className="profile-contact-edit">
                    <div className="profile-contact-edit-fields">
                      <input
                        type="tel"
                        className="profile-contact-input"
                        value={contactNumber}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                          // Prevent number starting with 0 - if user tries to enter 0, don't add it
                          if (digits.length === 1 && digits === '0') {
                            return; // Don't update state
                          }
                          setContactNumber(digits);
                          setError(''); // Clear any existing errors when user types
                        }}
                        placeholder="Enter 10 digits (e.g., 9876543210)"
                        maxLength="10"
                        disabled={isUpdatingContact}
                      />
                      {(() => {
                        const digits = contactNumber ? contactNumber.replace(/\D/g, '') : '';
                        const validation = contactNumber ? validateIndianPhoneNumber(contactNumber) : { isValid: true, error: null };
                        const statusColor = contactNumber && !validation.isValid ? '#d32f2f' : digits.length === 10 ? '#27ae60' : '#f59e0b';
                        const statusText = contactNumber ? `${digits.length}/10` : '-';
                        const statusIcon = !contactNumber ? '-' : validation.isValid ? '✓' : '✗';
                        return (
                          <>
                            <small className="profile-contact-meta" style={{ color: statusColor }}>
                              {statusIcon} {statusText}
                            </small>
                            {contactNumber && !validation.isValid && (
                              <small className="profile-contact-meta profile-contact-meta--error">
                                {validation.error}
                              </small>
                            )}
                            {contactNumber && validation.isValid && (
                              <small className="profile-contact-meta profile-contact-meta--hint">
                                {formatPhoneNumber(contactNumber)}
                              </small>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <div className="profile-contact-edit-actions">
                      <button
                        type="button"
                        className="profile-contact-icon-btn profile-contact-icon-btn--cancel"
                        onClick={handleCancelContactNumber}
                        disabled={isUpdatingContact}
                        title="Cancel"
                        aria-label="Cancel contact number edit"
                      >
                        ✕
                      </button>
                      <button
                        type="button"
                        className="profile-contact-icon-btn profile-contact-icon-btn--save"
                        onClick={handleSaveContactNumber}
                        disabled={isUpdatingContact || (contactNumber && !validateIndianPhoneNumber(contactNumber).isValid)}
                        title={isUpdatingContact ? 'Saving…' : 'Save'}
                        aria-label={isUpdatingContact ? 'Saving contact number' : 'Save contact number'}
                      >
                        {isUpdatingContact ? '…' : '✓'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="profile-contact-display">
                    <div className="profile-contact-display-value">
                      <div className="field-value">{contactNumber ? formatPhoneNumber(contactNumber) : '-'}</div>
                      {showContactSaveSuccess && (
                        <span className="profile-contact-saved" aria-hidden="true">✓</span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="profile-contact-edit-btn"
                      onClick={() => setIsEditingContactNumber(true)}
                      title="Edit contact number"
                    >
                      ✏️
                    </button>
                  </div>
                )}
              </div>

              <div className="info-field">
                <div className="field-header">
                  <span className="field-icon">🎭</span>
                  <span className="field-label">Role</span>
                </div>
                <div className="field-value">
                  <RoleBadge role={user.role} size="medium" />
                </div>
              </div>

              {assignedLocations.length > 0 && (
                <div className="info-field">
                  <div className="field-header">
                    <span className="field-icon">🏥</span>
                    <span className="field-label">Assigned Locations</span>
                  </div>
                  <div className="field-value">
                    {assignedLocations.length === 1 ? (
                      <div className="profile-scope-chips">
                        <span className="profile-scope-chip profile-scope-chip--location">
                          {assignedLocations[0].name}
                        </span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="profile-scope-view-btn profile-scope-view-btn--location"
                        aria-haspopup="dialog"
                        aria-expanded={showLocationsModal}
                        aria-label={`View all ${assignedLocations.length} assigned locations`}
                        onClick={() => setShowLocationsModal(true)}
                      >
                        <span className="profile-scope-view-btn__count" aria-hidden="true">
                          {assignedLocations.length}
                        </span>
                        <span className="profile-scope-view-btn__title">
                          locations
                        </span>
                        <span className="profile-scope-view-btn__arrow" aria-hidden="true">→</span>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {showDepartmentsField && (
                <div className="info-field">
                  <div className="field-header">
                    <span className="field-icon">🏢</span>
                    <span className="field-label">Assigned Departments</span>
                  </div>
                  <div className="field-value">
                    {isLocationScoped ? (
                      <div className="profile-scope-chips">
                        <span className="profile-scope-chip profile-scope-chip--all">
                          <span aria-hidden="true">🌐</span>
                          All Departments
                        </span>
                      </div>
                    ) : assignedDepartments.length === 0 ? (
                      <span className="profile-scope-empty">—</span>
                    ) : assignedDepartments.length === 1 ? (
                      <div className="profile-scope-chips">
                        <span className="profile-scope-chip profile-scope-chip--department">
                          <span aria-hidden="true">{assignedDepartments[0].icon || '🏢'}</span>
                          {assignedDepartments[0].name}
                        </span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="profile-scope-view-btn profile-scope-view-btn--department"
                        aria-haspopup="dialog"
                        aria-expanded={showDepartmentsModal}
                        aria-label={`View all ${assignedDepartments.length} assigned departments`}
                        onClick={() => setShowDepartmentsModal(true)}
                      >
                        <span className="profile-scope-view-btn__count" aria-hidden="true">
                          {assignedDepartments.length}
                        </span>
                        <span className="profile-scope-view-btn__title">
                          departments
                        </span>
                        <span className="profile-scope-view-btn__arrow" aria-hidden="true">→</span>
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="info-field">
                <div className="field-header">
                  <span className="field-icon">📅</span>
                  <span className="field-label">Member Since</span>
                </div>
                <div className="field-value">
                  {new Date(user.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              </div>
          </div>
        </div>

        {/* Password Change Section */}
        <div className="profile-settings-section">
          <div className="profile-settings-section-header">
            <h2 className="profile-settings-section-title">Security</h2>
            {!isChangingPassword && (
              <div className="action-palette">
                <button
                  type="button"
                  className="action-btn action-primary"
                  onClick={() => setIsChangingPassword(true)}
                >
                  <span className="action-icon">🔐</span>
                  <span className="action-text">Change Password</span>
                </button>
              </div>
            )}
          </div>

          {success && <div className="success-message">{success}</div>}

          {isChangingPassword && (
            <form onSubmit={handlePasswordSubmit} className="password-form">
              {error && <div className="error-message">{error}</div>}

              <div className="form-group">
                <label htmlFor="current_password">Current Password *</label>
                <input
                  type="password"
                  id="current_password"
                  name="current_password"
                  value={passwordData.current_password}
                  onChange={handlePasswordChange}
                  required
                  autoFocus
                  placeholder="Enter your current password"
                />
              </div>

              <div className="form-group">
                <label htmlFor="new_password">New Password *</label>
                <input
                  type="password"
                  id="new_password"
                  name="new_password"
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  required
                  minLength="8"
                  placeholder="Minimum 8 characters"
                />

                {passwordData.new_password && (
                  <div style={{ marginTop: '8px' }}>
                    <div style={{ display: 'flex', gap: '3px', marginBottom: '6px' }}>
                      {[0, 1, 2, 3, 4].map((index) => (
                        <div
                          key={index}
                          style={{
                            height: '4px',
                            flex: 1,
                            backgroundColor: index < passwordStrength
                              ? getPasswordStrengthColor(passwordStrength - 1)
                              : '#e0e0e0',
                            borderRadius: '2px'
                          }}
                        />
                      ))}
                    </div>
                    <small style={{ color: getPasswordStrengthColor(passwordStrength), fontWeight: '600' }}>
                      {getPasswordStrengthLabel(passwordStrength)}
                    </small>
                  </div>
                )}

                <small className="password-form-hint">
                  Must be at least 8 characters. Mix of uppercase, lowercase, numbers, and symbols recommended.
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="confirm_password">Confirm New Password *</label>
                <input
                  type="password"
                  id="confirm_password"
                  name="confirm_password"
                  value={passwordData.confirm_password}
                  onChange={handlePasswordChange}
                  required
                  minLength="8"
                  placeholder="Re-enter new password"
                />
                {passwordData.new_password && passwordData.confirm_password && passwordData.new_password !== passwordData.confirm_password && (
                  <small style={{ color: '#d32f2f', display: 'block', marginTop: '4px' }}>
                    ⚠️ Passwords do not match
                  </small>
                )}
                {passwordData.new_password && passwordData.confirm_password && passwordData.new_password === passwordData.confirm_password && (
                  <small style={{ color: '#27ae60', display: 'block', marginTop: '4px' }}>
                    ✓ Passwords match
                  </small>
                )}
              </div>

              <div className="password-form-actions">
                <button type="button" className="action-btn action-secondary" onClick={handleCancel}>
                  <span className="action-icon">✕</span>
                  <span className="action-text">Cancel</span>
                </button>
                <button type="submit" className="action-btn action-primary">
                  <span className="action-icon">✓</span>
                  <span className="action-text">Update Password</span>
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Assigned locations list (multi-location users) */}
      <Modal
        isOpen={showLocationsModal}
        onClose={() => setShowLocationsModal(false)}
        title="Assigned Locations"
        size="small"
        className="profile-scope-modal"
      >
        <p className="profile-scope-modal__intro" style={{ margin: '0 0 0.65rem' }}>
          You are assigned to {assignedLocations.length} locations.
        </p>
        <ul className="profile-scope-modal__list">
          {assignedLocations.map((location) => (
            <li key={location.id || location.name} className="profile-scope-chip profile-scope-chip--location">
              {location.name}
            </li>
          ))}
        </ul>
      </Modal>

      {/* Assigned departments list (multi-dept users) */}
      <Modal
        isOpen={showDepartmentsModal}
        onClose={() => setShowDepartmentsModal(false)}
        title="Assigned Departments"
        size="small"
        className="profile-scope-modal"
      >
        <p className="profile-scope-modal__intro" style={{ margin: '0 0 0.65rem' }}>
          You are assigned to {assignedDepartments.length} departments.
        </p>
        <ul className="profile-scope-modal__list">
          {assignedDepartments.map((dept) => (
            <li key={dept.id || dept.name} className="profile-scope-chip profile-scope-chip--department">
              <span aria-hidden="true">{dept.icon || '🏢'}</span>
              {dept.name}
            </li>
          ))}
        </ul>
      </Modal>

      {/* Remove Profile Picture Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRemovePictureConfirmation}
        entityName="Profile Picture"
        entityType="picture"
        action="delete"
        onConfirm={handleConfirmRemovePicture}
        onCancel={handleCancelRemovePicture}
        isLoading={isRemovingPicture}
        customMessage="Your profile picture will be removed. You can upload a new one anytime from this page."
      />
    </div>
  );
}

export default ProfileSettings;
